import { Singleton } from '@visiba/instantiation';
import {
	BrowserHistory,
	BrowserHistoryOptions,
	createBrowserHistory,
	createMemoryHistory,
	MemoryHistory,
	MemoryHistoryOptions,
	State,
} from 'history';
import { resolvePath } from 'react-router';

interface NavigateOption {
	replace?: boolean;
	state?: State;
}

@Singleton()
export class RouterService {
	private internalHistory: BrowserHistory | MemoryHistory | null = null;
	private v5_to_v6_patching_basename: Nullable<string> = null;
	public get history(): BrowserHistory | MemoryHistory {
		if (!this.internalHistory) throw new Error('History is null, initialize one by calling the `createBrowserHistory` method');

		return this.internalHistory;
	}

	constructor() {
		// Empty
	}

	// Text copied from https://github.com/ReactTraining/history/blob/master/packages/history/index.ts#L390
	/**
	 * Browser history stores the location in regular URLs. This is the standard for
	 * most web apps, but it requires some configuration on the server to ensure you
	 * serve the same app at multiple URLs.
	 */
	public createBrowserHistory(
		options?: BrowserHistoryOptions & {
			basename?: string;
		},
	): BrowserHistory {
		if (this.internalHistory == null) {
			this.internalHistory = createBrowserHistory(options);
			this.v5_to_v6_patching_basename ??= options?.basename ?? '';
		}

		return this.internalHistory;
	}

	// Text copied from https://github.com/ReactTraining/history/blob/master/packages/history/index.ts#L877
	/**
	 * Memory history stores the current location in memory. It is designed for use
	 * in stateful non-browser environments like tests and React Native.
	 */
	public createMemoryHistory(options?: MemoryHistoryOptions): MemoryHistory {
		if (this.internalHistory == null) {
			this.internalHistory = createMemoryHistory(options);
		}

		return this.internalHistory as MemoryHistory;
	}

	/**
	 * Navigates to a view using an relative route path.
	 */
	public navigateByUrl(to: string | number, options: NavigateOption = {}): void {
		// Implements https://github.com/ReactTraining/react-router/blob/dev/packages/react-router/index.tsx#L420
		if (typeof to === 'number') {
			this.history.go(to);

			return;
		}

		if (to[0] === '/') {
			to = (this.v5_to_v6_patching_basename ?? '') + to;
		}

		const path = resolvePath(to, this.internalHistory?.location.pathname);

		if (options.replace) {
			this.history.replace(path, options.state);

			return;
		}

		this.history.push(path, options.state);
	}
}
